import { Link } from 'react-router-dom';
import { buttonClass } from '@/components/ui/Button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useI18n } from '@/hooks/useI18n';
import { AvisoDeTraduccion } from './AvisoDeTraduccion';
import {
  ACCOUNT_DELETION_EMAIL,
  ACCOUNT_DELETION_ENTITY,
  inAppDeletionIsImmediate,
} from './tipos';
import styles from './AccountDeletionPage.module.css';

/**
 * Página pública requerida por Google Play (Data Safety: "Account deletion").
 * Un revisor la abre por URL directa, sin sesión iniciada — por eso no vive
 * detrás de ninguna ruta protegida ni depende de estado de auth.
 *
 * A propósito NO incluye ningún formulario que dispare un borrado real: es solo
 * informativa. Un endpoint de eliminación sin verificar identidad sería un
 * hueco de seguridad (cualquiera podría borrar la cuenta de cualquier teléfono
 * con solo conocerlo).
 *
 * ⚠️ Desde que el sitio detecta el idioma del navegador, **el revisor de Google
 * Play ve esta página en inglés**: su navegador casi seguro lo está. La versión
 * inglesa tiene que estar completa, no a medias.
 */
export function AccountDeletionPage() {
  const { t } = useI18n();
  const d = t.legal.eliminarCuenta;

  useDocumentMeta(`${d.titulo} — ILUMETRIA`, d.descripcion);

  return (
    <div className={styles.page}>
      <div className="ilu-container">
        <article className={styles.doc}>
          <h1 className={styles.title}>{d.titulo}</h1>

          <AvisoDeTraduccion />

          <p className={styles.intro}>{d.intro(ACCOUNT_DELETION_ENTITY)}</p>

          <section className={styles.block} aria-labelledby="section-app">
            <h2 id="section-app">{d.desdeLaApp}</h2>
            <ol className={styles.steps}>
              {d.pasos.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
            {inAppDeletionIsImmediate && <p className={styles.note}>{d.inmediata}</p>}
          </section>

          <section className={styles.block} aria-labelledby="section-no-app">
            <h2 id="section-no-app">{d.sinApp}</h2>
            <p>
              {d.sinAppTexto(ACCOUNT_DELETION_EMAIL)}{' '}
              <a
                href={`mailto:${ACCOUNT_DELETION_EMAIL}?subject=${encodeURIComponent(d.sinAppAsunto)}`}
                className="link-button"
              >
                {ACCOUNT_DELETION_EMAIL}
              </a>
            </p>
            {d.slaCorreo && <p className={styles.note}>{d.slaCorreo}</p>}
          </section>

          <section className={styles.block} aria-labelledby="section-data">
            <h2 id="section-data">{d.queSeElimina}</h2>
            <div className={styles.tableWrap} tabIndex={0}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">{d.tabla.seElimina}</th>
                    <th scope="col">{d.tabla.seConserva}</th>
                    <th scope="col">{d.tabla.porQue}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.filas.map((row) => (
                    <tr key={row.id}>
                      <td>{row.deleted}</td>
                      <td>{row.retained}</td>
                      <td>{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {d.notaConservacionLegal && <p className={styles.note}>{d.notaConservacionLegal}</p>}
          </section>

          <section className={styles.block}>
            <h2>{d.plazo}</h2>
            <p>
              {d.plazoTexto} {d.slaCorreo ?? d.plazoCorreoSinSla}
            </p>
          </section>

          <Link to="/" className={[buttonClass({ variant: 'outline' }), styles.back].join(' ')}>
            {t.legal.volverAlInicio}
          </Link>
        </article>
      </div>
    </div>
  );
}
